import { relations } from 'drizzle-orm';
import {
  index,
  jsonb,
  pgEnum,
  pgTable,
  primaryKey,
  serial,
  text,
  timestamp,
  uniqueIndex,
  varchar,
  integer,
} from 'drizzle-orm/pg-core';
import { typeIdDataType as typeId } from '../utils/typeid';
import { application } from './application';
import { event } from './event';
import { proxy } from './proxy';
import { realtimeInstance } from './realtime-instance';
import { accountScope } from './scope';

export const STATUS_RUNNING = 'running';
export const STATUS_ERRORED = 'errored';
export const STATUS_DISCONNECTED = 'disconnected';
export const STATUS_DEGRADED = 'degraded';
export const STATUS_PARTIAL = 'partial';
export const STATUS_PAUSED = 'paused';

export const ACCOUNT_STATUS = [
  STATUS_RUNNING,
  STATUS_ERRORED,
  STATUS_DISCONNECTED,
  STATUS_DEGRADED,
  STATUS_PARTIAL,
  STATUS_PAUSED,
] as const;

export type AccountStatus = (typeof ACCOUNT_STATUS)[number];

export const INITIAL_SYNC_STATUS_PENDING = 'pending';
export const INITIAL_SYNC_STATUS_RUNNING = 'running';
export const INITIAL_SYNC_STATUS_FAILED = 'failed';
export const INITIAL_SYNC_STATUS_COMPLETED = 'completed';

export const ACCOUNT_INITIAL_SYNC_STATUS = [
  INITIAL_SYNC_STATUS_PENDING,
  INITIAL_SYNC_STATUS_RUNNING,
  INITIAL_SYNC_STATUS_FAILED,
  INITIAL_SYNC_STATUS_COMPLETED,
] as const;

export type AccountInitialSyncStatus = (typeof ACCOUNT_INITIAL_SYNC_STATUS)[number];
export const ACCOUNT_PROVIDER = [
  'linkedin',
  'mock',
  'whatsapp',
  'google',
  'outlook',
  'imap',
  'telegram',
  'instagram',
  'facebook',
] as const;

export type Provider = (typeof ACCOUNT_PROVIDER)[number];

export const accountStatusEnum = pgEnum('account_status', ACCOUNT_STATUS);

export const account = pgTable(
  'account',
  {
    id: serial().primaryKey(),
    public_id: typeId('account', 'public_id').notNull(),
    name: varchar({ length: 255 }),
    created_at: timestamp({ withTimezone: true, mode: 'string' }).notNull().defaultNow(),
    application_id: integer()
      .references(() => application.id)
      .notNull(),
    account_scope_id: integer().references(() => accountScope.id, { onDelete: 'restrict' }),
    realtime_instance_id: integer()
      .references(() => realtimeInstance.id)
      .notNull(),
    provider: varchar({ length: 255 }).notNull().$type<Provider>(),
    authorization: text().notNull(),
    user_id: varchar({ length: 255 }).notNull(),
    status: accountStatusEnum().notNull(),
    initial_sync: jsonb().$type<{
      status: AccountInitialSyncStatus;
      started_at?: string;
    }>(),
    metadata: jsonb().notNull().default('{}').$type<{
      auto_proxy_country?: string;
      allow_country_fallback?: boolean;
      v1_account_id?: string;
      v1_instance_port?: number;
      connection_method?: 'credentials' | 'cookies' | 'unilogin';
      unilogin_session_version?: number;
      products_connection_status?: Record<string, 'running' | 'disconnected' | 'errored'>;
      products_display_names?: Record<string, string>;
      custom_data?: Record<string, string>;
    }>(),
    oauth_scope: text(),
    proxy_id: integer().references(() => proxy.id, { onDelete: 'restrict' }),
  },
  (t) => [
    uniqueIndex('account_public_id_uq').on(t.public_id),
    index('account_application_id_idx').on(t.application_id),
    index('account_account_scope_id_idx').on(t.account_scope_id),
    index('account_realtime_instance_id_idx').on(t.realtime_instance_id),
    index('account_proxy_id_idx').on(t.proxy_id),
    index('account_app_status_provider_name_idx').on(t.application_id, t.status, t.provider, t.name),
    index('account_user_provider_app_scope_idx').on(t.user_id, t.provider, t.application_id, t.account_scope_id),
  ],
);

export const accountsRelations = relations(account, ({ one, many }) => ({
  realtime_instance: one(realtimeInstance, {
    fields: [account.realtime_instance_id],
    references: [realtimeInstance.id],
  }),
  events: many(event),
  application: one(application, { fields: [account.application_id], references: [application.id] }),
  accountScope: one(accountScope, { fields: [account.account_scope_id], references: [accountScope.id] }),
  proxy: one(proxy, { fields: [account.proxy_id], references: [proxy.id] }),
}));

export const accountCursor = pgTable(
  'account_cursor',
  {
    account_id: integer()
      .references(() => account.id, { onDelete: 'cascade' })
      .notNull(),
    key: text('key').notNull(),
    key_hash: varchar('key_hash', { length: 64 }).notNull(),
    value: text('value'),
    updated_at: timestamp({ withTimezone: true, mode: 'string' }).notNull().defaultNow(),
  },
  (t) => [primaryKey({ columns: [t.account_id, t.key_hash] })],
);

export const accountRealtimeSync = pgTable(
  'account_realtime_sync',
  {
    account_id: integer('account_id')
      .references(() => account.id, { onDelete: 'cascade' })
      .notNull(),
    key: text('key').notNull(),
    key_hash: varchar('key_hash', { length: 64 }).notNull(),
    hash: text('hash').notNull(),
  },
  (t) => [primaryKey({ columns: [t.account_id, t.key_hash] })],
);

export type AccountRestrictionReason = 'free_trial_used' | 'payment_issue' | 'other';

export const accountRestricted = pgTable(
  'account_restricted',
  {
    user_id: varchar({ length: 255 }).notNull(),
    provider: varchar({ length: 255 }).notNull().$type<Provider>(),
    organization_id: typeId('organization', 'organization_id').notNull(), // Do not use the reference because we need to be able to delete the organization without deleting the restricted accounts
    created_at: timestamp({ withTimezone: true, mode: 'string' }).notNull().defaultNow(),
    expires_at: timestamp({ withTimezone: true, mode: 'string' }),
    reason: text('reason').$type<AccountRestrictionReason>(),
    name: varchar({ length: 255 }),
  },
  (t) => [
    primaryKey({ columns: [t.user_id, t.provider, t.organization_id] }),
    index('account_restricted_org_reason_idx').on(t.organization_id, t.reason),
  ],
);
